import { useState, useMemo } from 'react';
import HostLiveCard from './components/HostLiveCard';
import HostLiveSortBar from './components/HostLiveSortBar';
import { liveHosts, liveHostsStats, type HostSortMode } from '@/mocks/liveHosts';
import { liveRadiosStats } from '@/mocks/liveRadios';

export default function HostsLivePage() {
  const [sortMode, setSortMode] = useState<HostSortMode>('activity');

  const sortedHosts = useMemo(() => {
    const list = [...liveHosts];
    if (sortMode === 'activity') {
      list.sort((a, b) => b.activityScore - a.activityScore);
    } else if (sortMode === 'followers') {
      list.sort((a, b) => b.followers - a.followers);
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [sortMode]);

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20radio%20show%20host%20looking%20directly%20into%20the%20camera%20warmly%2C%20intimate%20broadcast%20studio%20setting%20with%20soft%20amber%20glow%2C%20microphone%20in%20foreground%20with%20shallow%20depth%20of%20field%2C%20warm%20authentic%20and%20charismatic%20atmosphere%2C%20editorial%20portrait%20photography%20with%20emotional%20connection%20and%20professional%20broadcast%20confidence%2C%20cozy%20creative%20studio%20environment%20with%20ambient%20lighting&width=1600&height=900&seq=hosts-live-hero-01&orientation=landscape"
          alt="Locutores en Vivo Ahora"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
            </span>
            <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-amber-500/80 px-3 py-1 rounded-full backdrop-blur-sm">
              EN CABINA
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Locutores en Vivo Ahora
          </h1>
          <p className="text-sm md:text-base text-background-50/75 max-w-lg leading-relaxed">
            Las voces que están haciendo radio en este instante. Descubrí quién está al aire y seguí a tus locutores favoritos.
          </p>
          <div className="flex items-center gap-5 mt-5">
            <div className="flex items-center gap-2 text-background-50/90 text-xs">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
              <span className="font-bold">{liveHostsStats.totalHosts} locutores</span>
            </div>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveHostsStats.totalRadios} radios</span>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{liveRadiosStats.totalLiveListeners.toLocaleString()} oyentes</span>
          </div>
        </div>
      </section>

      {/* STATS ROW */}
      <section className="bg-background-100 py-5">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex flex-wrap gap-5 items-center justify-center">
          {[
            { icon: 'ri-user-voice-line', label: 'Locutores al Aire', value: liveHostsStats.totalHosts.toString() },
            { icon: 'ri-group-line', label: 'Seguidores Totales', value: liveHostsStats.totalFollowers >= 1000 ? `${(liveHostsStats.totalFollowers / 1000).toFixed(1)}K` : liveHostsStats.totalFollowers.toString() },
            { icon: 'ri-radio-line', label: 'Radios Activas', value: liveHostsStats.totalRadios.toString() },
            { icon: 'ri-broadcast-line', label: 'Al Aire Normal', value: liveHostsStats.onAirCount.toString() },
            { icon: 'ri-mic-line', label: 'Shows en Vivo', value: liveHostsStats.liveShowCount.toString() },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10 space-y-6">
        <HostLiveSortBar
          currentSort={sortMode}
          onSortChange={setSortMode}
          totalCount={sortedHosts.length}
          totalRadios={liveHostsStats.totalRadios}
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {sortedHosts.map((host) => (
            <HostLiveCard key={host.id} host={host} />
          ))}
        </div>

        <section className="pt-8 pb-4 text-center">
          <p className="text-xs text-foreground-400">
            ¿Sos locutor y querés tu perfil?{' '}
            <a href="/register-radio" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
              Registrá tu radio en RadioFrecuenciaWeb
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}